import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Interactive floor plan.
 *
 * Renders the plan the admin drew in the "Plan" tab of the admin panel
 * (persisted via `GET /api/floor-plan`) instead of a hardcoded SVG.
 * Each element is either a `space` (rectangular room) or a `label`
 * (non-rentable annotation like "Entrée" / "Toilettes").
 *
 * Space elements with `linked_space_id` are clickable and drive the
 * booking flow via `onSelectSpace(space_id)`.
 */
export default function FloorPlanSVG({
  selectedSpace,
  onSelectSpace,
  bookedSpaces = [],
  spacePrices = {},
}) {
  const [elements, setElements] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [planRes, spacesRes] = await Promise.all([
          axios.get(`${API}/floor-plan`),
          axios.get(`${API}/spaces`),
        ]);
        if (cancelled) return;
        setElements(planRes.data || []);
        setSpaces(spacesRes.data || []);
      } catch (e) {
        // silent — the parent form still works without the visual plan
        console.warn('[FloorPlanSVG] failed to load plan:', e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Compute the SVG viewBox from the actual element positions so the
  // plan always fits regardless of how the admin arranged the rooms.
  const viewBox = useMemo(() => {
    if (elements.length === 0) return '0 0 900 600';
    let maxX = 0, maxY = 0;
    elements.forEach(el => {
      maxX = Math.max(maxX, (el.x || 0) + (el.width || 0));
      maxY = Math.max(maxY, (el.y || 0) + (el.height || 0));
    });
    return `0 0 ${Math.max(maxX + 20, 400)} ${Math.max(maxY + 20, 300)}`;
  }, [elements]);

  const spacesById = useMemo(
    () => Object.fromEntries(spaces.map(s => [s.space_id, s])),
    [spaces],
  );

  const getPrice = (spaceId) => {
    if (spacePrices[spaceId]?.hourly !== undefined) return spacePrices[spaceId].hourly;
    return spacesById[spaceId]?.hourly;
  };

  const getStatus = (el) => {
    // Non-space or unlinked rectangles are "facility" (non-rentable).
    if (el.type !== 'space' || !el.linked_space_id) return 'facility';
    if (selectedSpace === el.linked_space_id) return 'selected';
    if (bookedSpaces.includes(el.linked_space_id)) return 'booked';
    return 'available';
  };

  const styleForStatus = (status, baseColor) => {
    switch (status) {
      case 'selected':
        return { fill: '#1A3C34', stroke: '#0D1F1A', text: '#FFFFFF' };
      case 'booked':
        return { fill: '#D1D5DB', stroke: '#9CA3AF', text: '#6B7280' };
      case 'facility':
        return { fill: baseColor || '#E8E8E5', stroke: '#B0B0AD', text: '#1A3C34' };
      case 'available':
      default:
        // The admin picked a colour for each room — respect it.
        return { fill: baseColor || '#4CAF50', stroke: '#1A3C34', text: '#FFFFFF' };
    }
  };

  const handleClick = (el) => {
    if (el.type !== 'space' || !el.linked_space_id) return;
    if (bookedSpaces.includes(el.linked_space_id)) return;
    onSelectSpace(el.linked_space_id === selectedSpace ? null : el.linked_space_id);
  };

  const cursorForStatus = (status) => {
    if (status === 'facility') return 'default';
    if (status === 'booked') return 'not-allowed';
    return 'pointer';
  };

  return (
    <div className="relative w-full" data-testid="floor-plan-svg">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-4 text-sm font-body" data-testid="floor-plan-legend">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm bg-[#4CAF50]" />
          <span className="text-[#737373] font-semibold">Disponible</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm bg-[#D1D5DB]" />
          <span className="text-[#737373] font-semibold">Réservé</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm bg-[#1A3C34]" />
          <span className="text-[#737373] font-semibold">Sélectionné</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm bg-[#E8E8E5] border border-[#B0B0AD]" />
          <span className="text-[#737373] font-semibold">Commodités</span>
        </div>
      </div>

      {loading && (
        <div className="h-40 flex items-center justify-center text-sm text-[#737373]" data-testid="floor-plan-loading">
          Chargement du plan…
        </div>
      )}

      {!loading && elements.length === 0 && (
        <div className="h-40 flex items-center justify-center text-sm text-[#737373]" data-testid="floor-plan-empty">
          Aucun plan configuré. L'administrateur peut le dessiner dans le panel admin → Plan.
        </div>
      )}

      {!loading && elements.length > 0 && (
        <svg
          viewBox={viewBox}
          className="w-full h-auto border border-[#E2E2DF] rounded-md bg-white"
          role="img"
          aria-label="Plan interactif Flexi-location"
          preserveAspectRatio="xMidYMid meet"
          data-testid="floor-plan-svg-root"
        >
          <defs>
            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#F5F5F2" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect
            x="0" y="0"
            width={viewBox.split(' ')[2]}
            height={viewBox.split(' ')[3]}
            fill="url(#grid)"
          />

          {elements.map((el) => {
            const status = getStatus(el);
            const { fill, stroke, text } = styleForStatus(status, el.color);
            const isSpace = el.type === 'space' && el.linked_space_id;
            const price = isSpace ? getPrice(el.linked_space_id) : null;
            const info = isSpace ? spacesById[el.linked_space_id] : null;
            return (
              <g
                key={el.element_id}
                data-testid={`floor-plan-room-${el.linked_space_id || el.element_id}`}
                onClick={() => handleClick(el)}
                onMouseEnter={(e) => {
                  if (isSpace && status !== 'booked') {
                    const svgRect = e.currentTarget.closest('svg').getBoundingClientRect();
                    const gRect = e.currentTarget.getBoundingClientRect();
                    setTooltip({
                      x: gRect.left - svgRect.left + gRect.width / 2,
                      y: gRect.top - svgRect.top,
                      title: el.text || info?.name || '',
                      subtitle: info
                        ? `${info.capacity} pers. · ${price ?? info.hourly} EUR/h`
                        : (price != null ? `${price} EUR/h` : ''),
                    });
                  }
                }}
                onMouseLeave={() => setTooltip(null)}
                style={{ cursor: cursorForStatus(status) }}
              >
                {el.image_url ? (
                  <>
                    <clipPath id={`clip-${el.element_id}`}>
                      <rect x={el.x} y={el.y} width={el.width} height={el.height} rx="4" />
                    </clipPath>
                    <image
                      href={el.image_url}
                      x={el.x}
                      y={el.y}
                      width={el.width}
                      height={el.height}
                      preserveAspectRatio="xMidYMid slice"
                      clipPath={`url(#clip-${el.element_id})`}
                    />
                    <rect
                      x={el.x}
                      y={el.y}
                      width={el.width}
                      height={el.height}
                      fill={status === 'selected' ? 'rgba(26,60,52,0.55)' : 'rgba(0,0,0,0.15)'}
                      stroke={stroke}
                      strokeWidth={status === 'selected' ? 3 : 1.5}
                      rx="4"
                    />
                  </>
                ) : (
                  <rect
                    x={el.x}
                    y={el.y}
                    width={el.width}
                    height={el.height}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={status === 'selected' ? 3 : 1.5}
                    rx="4"
                    style={{ transition: 'fill 0.25s, stroke 0.25s' }}
                  />
                )}
                {/* Label — centered vertically */}
                <text
                  x={el.x + el.width / 2}
                  y={el.y + el.height / 2 - (price != null ? 6 : 0)}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={el.image_url ? '#FFFFFF' : text}
                  fontSize={el.font_size || 13}
                  fontFamily="Manrope, sans-serif"
                  fontWeight="700"
                  style={{ pointerEvents: 'none' }}
                >
                  {el.text || info?.name || ''}
                </text>
                {price != null && (
                  <text
                    x={el.x + el.width / 2}
                    y={el.y + el.height / 2 + 10}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={el.image_url ? '#FFFFFF' : text}
                    fontSize={(el.font_size || 13) - 1}
                    fontFamily="JetBrains Mono, monospace"
                    fontWeight="600"
                    style={{ pointerEvents: 'none' }}
                  >
                    {price} EUR/h
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div
          className="floor-tooltip"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y - 50}px`,
            transform: 'translateX(-50%)',
          }}
        >
          <div className="font-semibold">{tooltip.title}</div>
          {tooltip.subtitle && (
            <div className="text-xs opacity-80">{tooltip.subtitle}</div>
          )}
        </div>
      )}
    </div>
  );
}
